import {FC, useEffect, useState} from 'react'
import Card from './Card'
import EditButton from './EditButton'
import {toAbsoluteUrl} from '_metronic/helpers'
import {ToastContainer} from 'react-toastify'
import AddEditCustomControlModal from './modals/AddEditCustomControlModal'
import {useAuth} from 'app/modules/auth'
import {addDoc, collection, deleteDoc, doc, serverTimestamp} from '@firebase/firestore'
import db from 'services/firebase'
import Button from 'app/components/Button'
import Select from './Select'
import SelectedControlsModal from './modals/SelectedControlsModal'
import AddCSVModal from './modals/AddCSVModal'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {showSuccessToast} from 'app/components/ShowToast'
import {SingleValue} from 'react-select'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import CustomDrawer from './Drawer'
import Searchbar from 'app/components/Searchbar'

export type ControlDataType = {
  id: string
  title: string
  description: string
  category: string
  isShownInTrustCenter?: boolean
  isCustom?: boolean
}

type Props = {
  addedControls: ControlDataType[]
}

const Controls: FC<Props> = ({addedControls}) => {
  const {currentUser} = useAuth()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const [loading, setLoading] = useState(false)

  const [searchLength, setSearchLength] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredControls, setFilteredControls] = useState<ControlDataType[]>(addedControls)
  const [selectedControlCategory, setSelectedControlCategory] = useState<{
    label: string
    value: string
  } | null>()

  const [isCustomControlAddEditModalOpen, setIsCustomAddEditModalOpen] = useState(false)
  const [isSelectedControlsModalOpen, setIsSelectedControlsModalOpen] = useState(false)
  const [mountSelectedControlsModal, setMountSelectedControlsModal] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isAddCSVModalOpen, setIsAddCSVModalOpen] = useState(false)

  const [toEditCustomControl, setToEditCustomControl] = useState<ControlDataType>({
    id: '',
    title: '',
    category: '',
    description: '',
  })

  const [toDeleteCustomControl, setToDeleteCustomControl] = useState<ControlDataType>()
  const [isTooltipShown, setIsTooltipShown] = useState(false)

  useEffect(() => {
    let filteredData = addedControls.filter((control) =>
      control.title.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (selectedControlCategory?.value) {
      filteredData = filteredData.filter(
        (control) => control.category.toLowerCase() === selectedControlCategory?.value.toLowerCase()
      )
    }

    setFilteredControls(filteredData)

    if (addedControls.length !== 0) {
      setSearchLength(filteredData.length === 0)
    }
  }, [searchTerm, selectedControlCategory, addedControls])

  const categorizedControlsObj: Record<string, ControlDataType[]> = addedControls.reduce(
    (acc, control) => {
      const cateogry = control.category || 'Custom'
      if (!acc[cateogry]) {
        acc[cateogry] = []
      }
      acc[cateogry].push(control)
      return acc
    },
    {} as Record<string, ControlDataType[]>
  )

  const categorizedControlsArr: {category: string; controls: ControlDataType[]}[] = Object.entries(
    categorizedControlsObj
  )
    .map(([category, controls]) => ({
      category,
      controls,
    }))
    .sort((a, b) => a.category.localeCompare(b.category))

  const onCancel = () => {
    setIsDrawerOpen(false)
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }

  const removeCustomControl = async () => {
    try {
      setLoading(true)

      const tenantId = currentUser?.tenantId || ''
      const controlsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'controls',
        'all_controls'
      )

      await deleteDoc(doc(controlsRef, toDeleteCustomControl?.id))

      const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      await addDoc(activitiesRef, {
        message: `${toDeleteCustomControl?.title} custom control for trust center has been removed`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
        target: 'Trust Center',
        action: 'Custom control removed',
      })

      showSuccessToast({message: 'Custom control has been deleted successfully'})
    } catch (error) {
      console.error('Error while deleting custom control for trust center')
    } finally {
      setLoading(false)
      setIsDeleteModalOpen(false)
      setIsSelectedControlsModalOpen(true)
    }
  }

  const handleSetAddEditCustomControl = (control: ControlDataType) => {
    setIsSelectedControlsModalOpen(false)
    setIsCustomAddEditModalOpen(true)
    setToEditCustomControl(control)
  }

  const handleSetDeleteCustomControl = (control: ControlDataType) => {
    setIsSelectedControlsModalOpen(false)
    setIsDeleteModalOpen(true)
    setToDeleteCustomControl(control)
  }

  useEffect(() => {
    if (isTooltipShown) {
      document.body.style.overflowX = 'hidden'
    } else {
      document.body.style.overflowX = ''
    }
  }, [isTooltipShown])

  return (
    <div className=''>
      <CustomDrawer isDrawerOpen={isDrawerOpen} onCancel={onCancel} title='Controls'>
        <div
          style={{
            padding: '20px',
          }}
        >
          <p className='text-dark mb-6' style={{fontSize: '13px'}}>
            Select controls you’d like to show in your Trust Center. Please import your existing
            list if you haven’t added any controls to the platform yet.
          </p>

          <div className='d-flex flex-column' style={{gap: '16px'}}>
            <div className='d-flex justify-content-between align-items-center border-bottom border-gray-300 pb-1'>
              <span className='text-dark fs-6 fw-bold'>Controls</span>

              <Button
                btnType='transparent'
                onClick={() => {
                  setIsAddCSVModalOpen(true)
                }}
              >
                <img
                  alt='upload-icon'
                  src={toAbsoluteUrl('/media/icon/upload.svg')}
                  className='me-2'
                  style={{height: '18px', width: '18px'}}
                />
                <span>Import</span>
              </Button>
            </div>

            <div className='d-grid' style={{gridTemplateColumns: '65% auto', gap: '12px'}}>
              <div>
                <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} width='100%' />
              </div>

              <div>
                <Select
                  placeholder='Category'
                  options={categorizedControlsArr
                    .map((item) => ({
                      label: item.category,
                      value: item.category,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                  value={selectedControlCategory}
                  onChange={(value) => {
                    setSelectedControlCategory(value as SingleValue<{label: string; value: string}>)
                  }}
                />
              </div>
            </div>

            <div
              className='table-responsive'
              style={{
                borderRadius: '6.175px',
                border: '1px solid var(--bs-border-color)',
                maxHeight: '55vh',
                overflowY: 'auto',
              }}
            >
              <table className='table table-rounded gy-7 gs-7 mb-0 align-middle'>
                <thead>
                  <tr
                    className='fw-bold align-middle'
                    style={{
                      color: '#5A5A5A',
                      boxShadow: 'inset 0 -1px 0 var(--bs-border-color)',
                      backgroundColor: '#F9FAFB',
                      position: 'sticky',
                      top: 0,
                      zIndex: '10',
                    }}
                  >
                    <th>SELECTED CONTROLS FOR TRUST CENTER</th>
                    <th>
                      <div className='d-flex align-items-center justify-content-between'>
                        <span className='text-uppercase fw-bold' style={{color: '#5A5A5A'}}>
                          CATEGORY
                        </span>

                        <Button
                          style={{
                            height: '34px',
                            width: '34px',
                          }}
                          onClick={() => {
                            setIsSelectedControlsModalOpen(true)
                            setMountSelectedControlsModal(true)
                          }}
                        >
                          <img
                            alt='edit-icon'
                            src={toAbsoluteUrl('/media/icon/edit.svg')}
                            className=''
                            style={{height: '18px', width: '18px'}}
                          />
                        </Button>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredControls.length > 0 ? (
                    filteredControls.map((control, index) => (
                      <tr
                        style={{
                          borderBottom: `${
                            index !== filteredControls.length - 1
                              ? '1px solid var(--bs-border-color)'
                              : ''
                          }`,
                        }}
                        key={control.id}
                      >
                        <td>
                          <span className='text-dark fs-6 fw-medium'>{control.title}</span>
                        </td>

                        <td>
                          <span className='text-dark fs-6'>{control.category}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2}>
                        <div
                          className='w-100 d-flex align-items-center justify-content-center'
                          style={{height: '245px'}}
                        >
                          {searchLength ? (
                            <div className='d-flex flex-column align-items-center gap-4'>
                              <img src='/media/buttons/emptystate.svg' alt='' />
                              <p className='mb-0' style={{fontWeight: '600'}}>
                                No controls found
                              </p>
                              <p>
                                We couldn't find any controls that match your search. Maybe try a
                                different search!
                              </p>
                            </div>
                          ) : (
                            <div
                              className='w-100 d-flex justify-content-center align-items-center'
                              style={{height: '100%'}}
                            >
                              <div className='d-flex flex-column align-items-center gap-4'>
                                <div
                                  className='d-flex align-items-center justify-content-center'
                                  style={{
                                    backgroundColor: '#F3F4F6',
                                    borderRadius: '12px',
                                    height: '48px',
                                    width: '48px',
                                  }}
                                >
                                  <img
                                    alt='adjustments-horizontal-icon'
                                    src={toAbsoluteUrl('/media/icon/adjustments-horizontal.svg')}
                                    className=''
                                    style={{height: '32px', width: '32px'}}
                                  />
                                </div>

                                <span className='text-dark fs-6 fw-medium'>No controls added</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CustomDrawer>

      {mountSelectedControlsModal && (
        <SelectedControlsModal
          isModalOpen={isSelectedControlsModalOpen}
          hideModal={() => {
            setIsSelectedControlsModalOpen(false)
            setMountSelectedControlsModal(false)
          }}
          handleSetAddEditCustomControl={handleSetAddEditCustomControl}
          handleSetDeleteCustomControl={handleSetDeleteCustomControl}
          handleSetAddCSV={() => {
            setIsSelectedControlsModalOpen(false)
            setIsAddCSVModalOpen(true)
          }}
        />
      )}

      {isCustomControlAddEditModalOpen && (
        <AddEditCustomControlModal
          isModalOpen={isCustomControlAddEditModalOpen}
          hideModal={() => {
            setIsCustomAddEditModalOpen(false)
            setIsSelectedControlsModalOpen(true)
          }}
          isEditMode={Boolean(toEditCustomControl.id)}
          customControl={toEditCustomControl}
        />
      )}

      {isAddCSVModalOpen && (
        <AddCSVModal
          isModalOpen={isAddCSVModalOpen}
          hideModal={() => {
            setIsAddCSVModalOpen(false)
            setIsSelectedControlsModalOpen(true)
          }}
        />
      )}

      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => {
            setIsDeleteModalOpen(false)
            setIsSelectedControlsModalOpen(true)
          }}
          confirmBtnClickHandler={removeCustomControl}
          toDeleteItemTitle='custom control'
          isLoading={loading}
        />
      )}

      <Card
        bodyMaxHeight='100vh'
        title='Controls'
        actionComponent={
          <EditButton
            onClickHandler={() => {
              setIsDrawerOpen(true)
              const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
              document.body.style.overflow = 'hidden'
              document.body.style.paddingRight = `${scrollBarWidth}px`
            }}
          />
        }
      >
        {categorizedControlsArr.length > 0 ? (
          <div className='row gy-5 gx-10 me-0'>
            {categorizedControlsArr.map((list, index) => (
              <div key={index} className='col-6 col-xl-4 pe-0'>
                <div
                  key={index}
                  className='d-flex flex-column'
                  style={{
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #F3F4F6',
                    borderRadius: '8px',
                    gap: '20px',
                    padding: '20px',
                    height: '250px',
                    minWidth: '0',
                  }}
                >
                  <h4 className='w-100 fw-medium text-dark fs-6 mb-0 text-truncate'>
                    {list.category}
                  </h4>

                  <div className='d-flex flex-column' style={{gap: '16px'}}>
                    {list.controls.slice(0, 4).map((control) => (
                      <div
                        key={control.id}
                        className='d-flex align-items-center'
                        style={{gap: '6px'}}
                      >
                        <img
                          alt='cirle-check-icon'
                          src={toAbsoluteUrl('/media/icon/circle-check-gray-900.svg')}
                          className=''
                          style={{height: '16px', width: '16px'}}
                        />
                        <span className='text-dark text-truncate' style={{fontSize: '13px'}}>
                          {control.title}
                        </span>
                      </div>
                    ))}
                  </div>

                  {list.controls.length > 4 && (
                    <OverlayTrigger
                      placement='right'
                      overlay={
                        <Tooltip
                          id='tooltip-controls'
                          style={
                            {
                              '--bs-tooltip-max-width': '300px',
                              whiteSpace: 'pre-line',
                            } as React.CSSProperties
                          }
                        >
                          <ul
                            className='d-flex flex-column gap-1 m-0 p-0 ps-sm-5 ps-lg-4'
                            style={{maxHeight: '70vh', overflowY: 'hidden'}}
                          >
                            {list.controls.slice(4).map((control) => (
                              <li
                                key={control.id}
                                className='text-start'
                                style={{fontSize: '13px'}}
                              >
                                {control.title.trim()}
                              </li>
                            ))}
                          </ul>
                        </Tooltip>
                      }
                    >
                      <span
                        className='text-dark fw-medium'
                        style={{fontSize: '12px', width: 'fit-content'}}
                        onMouseEnter={() => setIsTooltipShown(true)}
                        onMouseLeave={() => setIsTooltipShown(false)}
                      >
                        +{`${list.controls.length - 4}`}&nbsp;more
                      </span>
                    </OverlayTrigger>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className='w-100 d-flex justify-content-center align-items-center'
            style={{height: '300px'}}
          >
            <div className='d-flex flex-column align-items-center gap-4'>
              <div
                className='d-flex align-items-center justify-content-center'
                style={{
                  backgroundColor: '#F3F4F6',
                  borderRadius: '12px',
                  height: '48px',
                  width: '48px',
                }}
              >
                <img
                  alt='adjustments-horizontal-icon'
                  src={toAbsoluteUrl('/media/icon/adjustments-horizontal.svg')}
                  className=''
                  style={{height: '32px', width: '32px'}}
                />
              </div>

              <span className='text-dark fs-6 fw-medium'>No controls added</span>
            </div>
          </div>
        )}
      </Card>

      <ToastContainer />
    </div>
  )
}

export default Controls
